import { useAuth } from "../context/auth";
import { verifyAuth } from "../services/auth";
import CheckIcon from "../assets/checkIcon.svg";
import { LoadingCircle } from "./Common/loading";
import { useStepStore } from "../stores/stepStore";
import { getProfile } from "../services/mortgageDetails";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserMortgageProfileStore } from "../stores/userProfileStore";
import { usePersonalInfoStore } from "../stores/personalInformationStore";
import { MortgageStatusEnum } from "../interfaces/mortgageDetailsInterface";
import {
  useRef,
  useState,
  useEffect,
  type FormEvent,
  type KeyboardEvent,
} from "react";

const VerifyView = () => {
  const [code, setCode] = useState<string>("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [loading, setLoading] = useState(false);
  const { email } = usePersonalInfoStore();
  const navigate = useNavigate();
  const { setAuthenticated } = useAuth();
  const location = useLocation();
  const { setUserMortgageProfile } = useUserMortgageProfileStore();
  const setStep = useStepStore((state) => state.setStep);

  useEffect(() => {
    if (location.state?.origin !== "emailView") {
      navigate("/");
    }
  }, [location, navigate]);

  const handleInputChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) {
      return;
    }

    const newCode = code.split("");
    newCode[index] = value;
    setCode(newCode.join(""));

    if (value && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && index > 0 && !code[index]) {
      inputRefs.current[index - 1]?.focus();
      setCode((prevCode) => {
        const newCode = prevCode.split("");
        newCode[index - 1] = "";
        return newCode.join("");
      });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await verifyAuth(email, code);

      const response = await getProfile();
      setUserMortgageProfile(response);
      setLoading(false);
      setAuthenticated(true);
      if (response?.status === MortgageStatusEnum.PendingDocuments) {
        setStep(8);
        navigate("/documents");
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Failed to verify auth:", error);
      setLoading(false);
      navigate("/login/email");
    }
  };

  return (
    <section className="absolute inset-0 h-full">
      <div className="lg:flex h-full">
        <div className="hidden w-full max-w-md p-12 lg:h-full lg:block bg-z-prussian-blue" />

        <div className="flex items-center mx-auto md:w-[42rem] px-4 md:px-8 xl:px-0 h-full">
          <div className="w-full">
            <ol className="lg:flex gap-4 items-center mb-6 text-sm hidden font-medium text-center text-gray-500 lg:mb-12 sm:text-base">
              <li className="text-z-prussian-blue flex flex-col items-center space-y-1">
                <img src={CheckIcon} alt="" />
                <span>Email Address</span>
              </li>
              <hr className="h-[1.8px] w-[60px] my-8 bg-[#E5E7EB] border-0" />
              <li className="text-z-prussian-blue flex flex-col items-center space-y-1">
                <img src={CheckIcon} alt="" />
                <span>Verify Email Address</span>
              </li>
              <hr className="h-[1.8px] w-[60px] my-8 bg-[#E5E7EB] border-0" />
              <li className="text-z-prussian-blue flex flex-col items-center space-y-1">
                <img src={CheckIcon} alt="" />
                <span>Begin Form</span>
              </li>
            </ol>

            <h1 className="mb-2 text-2xl font-extrabold text-gray-900">
              Verify your email address
            </h1>
            <p className="font-light text-gray-500">
              We emailed you a six-digit code to{" "}
              <span className="font-medium text-gray-900 ">{email}</span>. Enter
              the code below to confirm your email address.
            </p>

            {loading && (
              <div className="w-full flex items-center justify-center">
                <LoadingCircle />
              </div>
            )}
            {!loading && (
              <form onSubmit={handleSubmit}>
                <div className="flex my-4 space-x-2 sm:space-x-4 md:my-6">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="number"
                      maxLength={1}
                      value={code[index] || ""}
                      onChange={(e) => handleInputChange(e.target.value, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      className="w-12 h-12 py-3 text-2xl appearance-none font-extrabold text-center text-gray-900 bg-white border border-gray-300 rounded-lg sm:py-4 sm:text-4xl sm:w-16 sm:h-16 focus:ring-primary-500 focus:border-primary-500"
                      required={true}
                    />
                  ))}
                </div>

                <p className="p-4 mb-4 text-sm text-gray-500 bg-gray-50 rounded-lg">
                  Make sure to keep this window open while checking your inbox
                </p>

                <button
                  type="submit"
                  className="w-full px-5 py-2.5 text-sm font-medium text-white bg-z-prussian-blue rounded-lg sm:py-3.5 text-center"
                >
                  Verify
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export { VerifyView };
